/**
 * v1.60.3 — Helper centralizzato per POST finanziari idempotenti.
 *
 * Endpoint coperti:
 *   • POST /api/payments             (pagamento su spesa)
 *   • POST /api/ar/collections       (incasso su scadenza cliente)
 *   • POST /api/ar/daily-receipts    (incasso ordinario diretto)
 *
 * Uso tipico in un dialog:
 *
 *   const [idemKey, setIdemKey] = useState(() => newIdempotencyKey());
 *   const onOpen = () => setIdemKey(newIdempotencyKey());
 *
 *   const submit = async () => {
 *     try {
 *       const saved = await postFinancialMovement(
 *         "/ar/daily-receipts", payload, { idempotencyKey: idemKey }
 *       );
 *       toast.success("Salvato");
 *       // la chiave viene "consumata": genera una nuova per la prossima op
 *       setIdemKey(newIdempotencyKey());
 *     } catch (e) {
 *       // e.isIdempotencyConflict === true → 409 (payload diverso, stessa key)
 *       // altrimenti errore standard; la chiave resta invariata per il retry.
 *     }
 *   };
 *
 * Regole ciclo di vita:
 *   1. Apertura dialog per nuova operazione → newIdempotencyKey()
 *   2. Primo salvataggio → passa la chiave
 *   3. Errore di rete/timeout → NON rigenerare, si può ritentare
 *   4. Errore di validazione client-side → chiave invariata
 *   5. Salvataggio riuscito → nuova chiave per la prossima operazione
 *   6. Chiusura dialog senza salvare → nuova chiave alla riapertura
 */
import { client, unwrap } from "@/lib/api-base";

// Endpoint su cui la protezione è attiva lato backend (services/idempotency.py).
const IDEMPOTENT_ENDPOINTS = new Set([
  "/payments",
  "/ar/collections",
  "/ar/daily-receipts",
]);

/**
 * Genera un UUID v4 come chiave idempotente.
 * Usa crypto.randomUUID quando disponibile; fallback compatibile su
 * ambienti più vecchi (es. Playwright headless senza secure context).
 */
export function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback RFC4122 v4-like (non crittograficamente forte, ma sufficiente
  // per finestre di 5 minuti / TTL server).
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * POST idempotente. Se `idempotencyKey` non è fornita, ne genera una nuova.
 * Il chiamante DEVE mantenerla invariata attraverso i retry della STESSA
 * operazione. La chiave deve essere diversa per due tentativi realmente
 * distinti (es. secondo incasso parziale).
 *
 * @param {string} url  URL relativo (senza `/api`), es. "/ar/collections".
 *                      Solo gli endpoint in IDEMPOTENT_ENDPOINTS invieranno
 *                      effettivamente l'header — su URL non idempotenti
 *                      questa funzione è equivalente a un POST normale.
 * @param {object} payload
 * @param {{idempotencyKey?: string}} opts
 * @returns {Promise<{data: any, idempotencyKey: string, cached: boolean}>}
 */
export async function postFinancialMovement(url, payload, opts = {}) {
  const idempotencyKey = opts.idempotencyKey || newIdempotencyKey();
  const shouldSendHeader = IDEMPOTENT_ENDPOINTS.has(url);
  const config = shouldSendHeader
    ? { headers: { "Idempotency-Key": idempotencyKey } }
    : {};

  try {
    const res = await client.post(url, payload, config);
    return {
      data: res.data,
      idempotencyKey,
      // Nel backend attuale la risposta cached ha status 200 mentre la
      // creazione originale ha 201. Il consumer non deve differenziare
      // il comportamento UX (entrambi = successo), ma esponiamo il flag
      // per eventuali logging/telemetria.
      cached: res.status === 200,
    };
  } catch (err) {
    // 409 Conflict = riuso key con payload diverso. Segnaliamo con flag
    // esplicito per permettere UX dedicata (vedi §4 delle specifiche v1.60.3).
    if (err?.response?.status === 409) {
      const detail = err.response.data?.detail || "";
      if (typeof detail === "string" && detail.includes("Idempotency-Key")) {
        const wrapped = new Error(
          "La stessa operazione è già stata inviata con dati differenti. " +
          "Verifica i dati e ripeti il salvataggio.",
        );
        wrapped.isIdempotencyConflict = true;
        wrapped.originalError = err;
        throw wrapped;
      }
    }
    throw err;
  }
}

/**
 * Wrapper zucchero: analogo a postFinancialMovement ma unwrappa direttamente
 * il body (senza esporre {data, idempotencyKey}). Utile quando il chiamante
 * non ha bisogno di sapere se è stato cached — il retry avviene comunque
 * con la stessa key esplicita.
 */
export async function postFinancialMovementSimple(url, payload, opts = {}) {
  const { data } = await postFinancialMovement(url, payload, opts);
  return data;
}
